/**
 * The single source of truth for the backend base URL.
 *
 * Why this exists: REACT_APP_SERVER_URL is set per Netlify site (dev / staging /
 * main) and nothing stops the value from carrying a trailing slash. Before this
 * module, ~24 modules read the env var directly and only some of them trimmed
 * it, so the very same admin panel produced both "/api/events-managements" and
 * "//api/events-managements" depending on which module built the URL. Flask
 * tolerates the double slash (200 either way), but it pollutes the Heroku router
 * logs and makes any path-prefix matching unreliable.
 *
 * Rule: never read process.env.REACT_APP_SERVER_URL anywhere else. Import
 * SERVER_URL (already normalized, never ends with "/") or call serverPath().
 */

/** Trim trailing slashes from a base URL. Exported for tests. */
export const normalizeBaseUrl = (raw) => String(raw || "").trim().replace(/\/+$/, "");

/**
 * Join a base URL and a path with exactly one slash between them.
 * Safe against a base that ends with "/" and a path that does or doesn't
 * start with one. Replaces the six near-identical joinUrl helpers that had been
 * copy-pasted around the admin panel, each with slightly different edge-case
 * behavior — keep it at one.
 */
export const joinUrl = (base, path = "") => {
  const baseUrl = normalizeBaseUrl(base);
  if (!path) return baseUrl;
  const pathUrl = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${pathUrl}`;
};

/**
 * True when running inside the Windows event-workstation shell. The native
 * runtime (parent's Electron preload) injects `window.minimeDesktop` at
 * document-start, before any bundle script evaluates — so this is already
 * decided by the time SERVER_URL below is computed. In a normal web build,
 * or in the test env (jsdom without the bridge), it is false.
 */
export const isDesktopRuntime = () =>
  typeof window !== "undefined" &&
  !!(window.minimeDesktop && window.minimeDesktop.isDesktop);

/**
 * The backend origin, guaranteed not to end with a slash. May be "" in tests.
 *
 * Desktop: all backend calls are relative and go through the native
 * authenticated proxy served at the app's own origin (http://localhost:3000),
 * so the base is window.location.origin. This applies ONLY when the desktop
 * bridge is present — normal web builds keep the existing per-site
 * REACT_APP_SERVER_URL behavior untouched.
 */
export const SERVER_URL = isDesktopRuntime()
  ? normalizeBaseUrl(window.location.origin)
  : normalizeBaseUrl(process.env.REACT_APP_SERVER_URL);

/** Build an absolute backend URL: serverPath("/api/faces") or serverPath("api/faces"). */
export const serverPath = (path = "") => joinUrl(SERVER_URL, path);
